(() => {
  const root = window.Shopify?.routes?.root || '/';
  const liveRegion = document.querySelector('[data-live-region]');

  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 50);
  };

  const escapeHtml = (value = '') => {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'ZAR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-ZA', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  };

  const header = document.querySelector('[data-header-wrapper]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 20);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  const initAnnouncementMarquees = (scope = document) => {
    const marquees = [];
    if (scope.matches?.('[data-announcement-marquee]')) marquees.push(scope);
    scope.querySelectorAll?.('[data-announcement-marquee]').forEach((marquee) => marquees.push(marquee));

    marquees.forEach((marquee) => {
      if (marquee.dataset.marqueeReady === 'true') return;
      const track = marquee.querySelector('.announcement-bar__track');
      const firstGroup = marquee.querySelector('.announcement-bar__group');
      if (!track || !firstGroup || typeof track.animate !== 'function') return;

      marquee.dataset.marqueeReady = 'true';
      track.style.animation = 'none';
      let animation;
      let resizeTimer;
      const duration = Math.max(12000, (Number(marquee.dataset.announcementDuration) || 28) * 1000);

      const startMarquee = () => {
        const distance = firstGroup.getBoundingClientRect().width;
        if (distance < 1) {
          window.requestAnimationFrame(startMarquee);
          return;
        }
        animation?.cancel();
        animation = track.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(-${distance}px, 0, 0)` },
          ],
          { duration, iterations: Infinity, easing: 'linear' },
        );
      };

      marquee.addEventListener('focusin', () => animation?.pause());
      marquee.addEventListener('focusout', () => animation?.play());

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(() => {
          window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(startMarquee, 100);
        });
        resizeObserver.observe(firstGroup);
      }

      window.requestAnimationFrame(startMarquee);
    });
  };

  initAnnouncementMarquees();
  document.addEventListener('shopify:section:load', (event) => initAnnouncementMarquees(event.target));

  const mobileNav = document.querySelector('[data-mobile-nav]');
  const mobileMenuButton = document.querySelector('[data-mobile-open]');
  const openMobileMenu = () => {
    if (!mobileNav) return;
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    mobileMenuButton?.setAttribute('aria-expanded', 'true');
    mobileNav.querySelector('[data-mobile-close]')?.focus();
  };
  const closeMobileMenu = () => {
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
    mobileMenuButton?.setAttribute('aria-expanded', 'false');
    document.querySelector('[data-mobile-open]')?.focus();
  };
  document.querySelector('[data-mobile-open]')?.addEventListener('click', openMobileMenu);
  document.querySelector('[data-mobile-close]')?.addEventListener('click', closeMobileMenu);
  mobileNav?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMobileMenu));

  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerItems = document.querySelector('[data-cart-drawer-items]');
  const drawerFooter = document.querySelector('[data-cart-drawer-footer]');

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((element) => {
      element.textContent = `(${count})`;
    });
  };

  const renderCart = (cart) => {
    updateCartCount(cart.item_count);
    if (!drawerItems || !drawerFooter) return;

    if (!cart.items.length) {
      drawerItems.innerHTML = `
        <div class="empty-state">
          <p>Your bag is empty.</p>
          <a class="button" href="${root}#sticks">Continue shopping</a>
        </div>`;
      drawerFooter.classList.add('is-hidden');
      return;
    }

    drawerItems.innerHTML = cart.items.map((item) => {
      const fallbackImage = window.CRASH_THEME?.assets?.[item.handle] || '';
      const imageSource = item.image || fallbackImage;
      const image = imageSource
        ? `<img src="${escapeHtml(imageSource)}" alt="${escapeHtml(item.product_title)}" width="180" height="270" loading="lazy">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHtml(item.variant_title)}</p>`
        : '';
      return `
        <div class="drawer-item" data-line-key="${escapeHtml(item.key)}">
          <a href="${escapeHtml(item.url)}" class="drawer-item__image">${image}</a>
          <div>
            <a class="drawer-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variant}
            <p>${formatMoney(item.final_line_price)}</p>
            <div class="quantity-selector quantity-selector--small" data-drawer-quantity>
              <button type="button" data-cart-drawer-change="${escapeHtml(item.key)}" data-change-to="${Math.max(0, item.quantity - 1)}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity">−</button>
              <input type="number" value="${item.quantity}" min="0" aria-label="Quantity for ${escapeHtml(item.product_title)}" data-cart-drawer-input="${escapeHtml(item.key)}">
              <button type="button" data-cart-drawer-change="${escapeHtml(item.key)}" data-change-to="${item.quantity + 1}" aria-label="Increase ${escapeHtml(item.product_title)} quantity">+</button>
            </div>
            <button class="text-link" type="button" data-cart-remove="${escapeHtml(item.key)}">Remove</button>
          </div>
        </div>`;
    }).join('');

    drawerFooter.classList.remove('is-hidden');
    const total = drawerFooter.querySelector('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
  };

  const getCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  const openDrawer = async () => {
    if (!drawer) {
      window.location.href = `${root}cart`;
      return;
    }
    try { await refreshCart(); } catch (_error) { /* Server-rendered content remains available. */ }
    drawer.hidden = false;
    document.body.classList.add('drawer-open');
    drawer.querySelector('[data-drawer-close]')?.focus();
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('drawer-open');
  };

  document.querySelectorAll('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      openDrawer();
    });
  });

  document.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', closeDrawer));

  const changeDrawerItem = async (key, quantity, control) => {
    if (control) control.disabled = true;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('Unable to update item');
      renderCart(await response.json());
      announce(quantity === 0 ? 'Item removed from your bag.' : 'Bag updated.');
    } catch (_error) {
      if (control) control.disabled = false;
      announce('Something went wrong. Please try again.');
    }
  };

  drawer?.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      await changeDrawerItem(removeButton.dataset.cartRemove, 0, removeButton);
      return;
    }
    const quantityButton = event.target.closest('[data-cart-drawer-change]');
    if (!quantityButton) return;
    await changeDrawerItem(quantityButton.dataset.cartDrawerChange, Number(quantityButton.dataset.changeTo), quantityButton);
  });

  drawer?.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-drawer-input]');
    if (!input) return;
    await changeDrawerItem(input.dataset.cartDrawerInput, Math.max(0, Number(input.value || 0)), input);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const originalText = submit?.textContent;
    if (submit) {
      submit.disabled = true;
      submit.textContent = 'Adding…';
    }
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.description || 'Unable to add product');
      }
      await openDrawer();
      announce('Product added to your bag.');
    } catch (error) {
      announce(error.message);
      window.alert(error.message);
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.textContent = originalText;
      }
    }
  });

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-quantity-change]');
    if (!control) return;
    const wrapper = control.closest('[data-quantity]');
    const input = wrapper?.querySelector('input[type="number"]');
    if (!input) return;
    const step = Number(control.dataset.quantityChange);
    const min = Number(input.min || 1);
    const max = Number(input.max || 999);
    input.value = Math.min(max, Math.max(min, Number(input.value || min) + step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const viewport = gallery.querySelector('.product-gallery__viewport');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
    const previous = gallery.querySelector('[data-gallery-previous]');
    const next = gallery.querySelector('[data-gallery-next]');
    const counter = gallery.querySelector('[data-gallery-current]');
    if (!viewport || slides.length < 2) return;

    let activeIndex = 0;
    let touchStartX = 0;
    viewport.tabIndex = 0;

    const showSlide = (requestedIndex, focusThumbnail = false) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (!isActive) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
      thumbnails.forEach((thumbnail, index) => {
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-selected', isActive ? 'true' : 'false');
        thumbnail.tabIndex = isActive ? 0 : -1;
      });
      if (counter) counter.textContent = String(activeIndex + 1).padStart(2, '0');
      if (focusThumbnail) thumbnails[activeIndex]?.focus();
    };

    previous?.addEventListener('click', () => showSlide(activeIndex - 1));
    next?.addEventListener('click', () => showSlide(activeIndex + 1));
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => showSlide(index));
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        showSlide(index + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
    });
    viewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      showSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    viewport.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX || 0;
    }, { passive: true });
    viewport.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
      if (Math.abs(distance) > 45) showSlide(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });
  });

  document.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const select = productRoot.querySelector('[data-variant-select]');
    const json = productRoot.querySelector('[data-product-json]');
    if (!select || !json) return;
    let productData;
    try { productData = JSON.parse(json.textContent); } catch (_error) { return; }
    const price = productRoot.querySelector('[data-product-price]');
    const compare = productRoot.querySelector('[data-product-compare]');
    const addButton = productRoot.querySelector('[data-add-to-cart]');
    const mobileAddButton = productRoot.querySelector('.mobile-purchase-bar [data-purchase-only]');
    const mobilePrice = productRoot.querySelector('[data-mobile-product-price]');
    const availability = productRoot.querySelector('[data-product-availability]');
    const sku = productRoot.querySelector('[data-product-sku]');

    select.addEventListener('change', () => {
      const variant = productData.variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      const hasConfirmedPrice = variant.price > 0;
      productRoot.classList.toggle('is-catalogue', !hasConfirmedPrice);
      productRoot.querySelectorAll('[data-purchase-only]').forEach((element) => { element.hidden = !hasConfirmedPrice; });
      productRoot.querySelectorAll('[data-enquiry-only]').forEach((element) => { element.hidden = hasConfirmedPrice; });
      if (price) price.textContent = hasConfirmedPrice ? formatMoney(variant.price) : 'ENQUIRE FOR PRICE';
      if (mobilePrice) mobilePrice.textContent = hasConfirmedPrice ? formatMoney(variant.price) : 'ENQUIRE';
      if (compare) {
        compare.textContent = hasConfirmedPrice && variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(hasConfirmedPrice && variant.compare_at_price > variant.price);
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to bag' : 'Sold out';
      }
      if (mobileAddButton) {
        mobileAddButton.disabled = !variant.available;
        mobileAddButton.textContent = variant.available ? 'ADD TO BAG' : 'SOLD OUT';
      }
      if (availability) {
        availability.classList.toggle('is-unavailable', !variant.available);
        availability.innerHTML = `<span aria-hidden="true"></span>${variant.available ? 'Available' : 'Currently unavailable'}`;
      }
      if (sku) sku.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    });
  });

  document.querySelectorAll('product-recommendations').forEach(async (element) => {
    const url = element.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('product-recommendations');
      if (content?.innerHTML.trim()) element.innerHTML = content.innerHTML;
    } catch (_error) { /* Recommendations are optional. */ }
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  document.querySelectorAll('[data-filter-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const filters = document.querySelector('[data-filters]');
      filters?.classList.toggle('is-open');
      button.setAttribute('aria-expanded', filters?.classList.contains('is-open') ? 'true' : 'false');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeDrawer();
    closeMobileMenu();
  });

  const revealElements = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && revealElements.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealElements.forEach((element) => observer.observe(element));
  } else {
    revealElements.forEach((element) => element.classList.add('is-visible'));
  }
})();
